import { Suspense } from 'react';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { faChevronRight, faDownload, faMailbox, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { fetchLetterProofCheckoutDetails, fetchLetterProofs, sendLetterProof } from 'api/engage';
import { POSTAGE_TYPES } from 'assets/js/pages/engage/letters/constants';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import * as ReadOnlyPaymentDetails from 'assets/js/pages/transactions/ReadOnlyPaymentDetails';
import { PaymentProcessorIcon } from 'assets/js/pages/transactions/TransactionMethod';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isoDateFromServer } from 'helpers/dates';
import { capitalize } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { PolymorphicTransaction } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const LetterOrderedProofs = () => {
    const message = useToast();
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { data: proofs } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageProofs(accountId, letter.id),
        queryFn: () => fetchLetterProofs(accountId, letter.id),
    });
    const hasOrderedProofs = proofs.data.length > 0;

    const { mutate: retryProof, isPending: isRetryingProofOrder } = useMutation({
        mutationFn: (mailPieceId: number) => sendLetterProof(accountId, letter.id, mailPieceId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessageProofs(accountId, letter.id),
            });
            message.success('Proof ordered successfully');
        },
        onError: (error: GivebutterAxiosError) => {
            message.error(
                error.response?.data.message ?? (
                    <>
                        <span className="text-semibold">Whoops.</span> Something went wrong. Please
                        contact support.
                    </>
                ),
            );
        },
    });

    return (
        <div className="flex flex-col gap-6">
            {!hasOrderedProofs && (
                <div className="flex min-h-48 w-full flex-col items-center justify-center gap-4 text-center">
                    <BoxIcon size="lg" variant="secondary">
                        <FontAwesomeIcon icon={faMailbox} />
                    </BoxIcon>
                    <div>
                        <div className="font-medium">No proofs have been sent</div>
                        <div className="text-sm">
                            Purchase a mail proof to see details of the order
                        </div>
                    </div>
                </div>
            )}
            {hasOrderedProofs &&
                proofs.data.map(proof => (
                    <div key={proof.id}>
                        <div className={'flex flex-wrap gap-8'}>
                            <div className={'flex flex-1 flex-col gap-2'}>
                                <p className={'font-medium text-blue-700'}>Order #{proof.id}</p>
                                <div className={'flex gap-2'}>
                                    <p className={'w-1/4 text-sm font-medium text-gray-900'}>
                                        Recipient
                                    </p>
                                    <p className={'w-3/4 text-sm text-gray-600'}>
                                        {proof.to_address_snapshot.name}
                                    </p>
                                </div>
                                <div className={'flex gap-2'}>
                                    <p className={'w-1/4 text-sm font-medium text-gray-900'}>
                                        Recipient address
                                    </p>
                                    <p className={'w-3/4 text-sm text-gray-600'}>
                                        {proof.to_address_snapshot.address_line1}
                                        <br />
                                        {proof.to_address_snapshot.address_line2 && (
                                            <>
                                                {proof.to_address_snapshot.address_line2}
                                                <br />
                                            </>
                                        )}
                                        {proof.to_address_snapshot.address_city},{' '}
                                        {proof.to_address_snapshot.address_state}{' '}
                                        {proof.to_address_snapshot.address_zip}
                                    </p>
                                </div>
                                <div className={'flex gap-2'}>
                                    <p className={'w-1/4 text-sm font-medium text-gray-900'}>
                                        Type
                                    </p>
                                    <p className={'w-3/4 text-sm text-gray-600'}>
                                        {capitalize(proof.page_type)}
                                    </p>
                                </div>
                                <div className={'flex gap-2'}>
                                    <p className={'w-1/4 text-sm font-medium text-gray-900'}>
                                        Shipping speed
                                    </p>
                                    <p className={'w-3/4 text-sm text-gray-600'}>
                                        {POSTAGE_TYPES[proof.postage_type].label}
                                    </p>
                                </div>
                                <div className={'flex gap-2'}>
                                    <p className={'w-1/4 text-sm font-medium text-gray-900'}>
                                        Date
                                    </p>
                                    <p className={'w-3/4 text-sm text-gray-600'}>
                                        {isoDateFromServer(proof.created_at).toFormat(
                                            'LLLL dd, yyyy',
                                        )}
                                    </p>
                                </div>
                                <div className={'flex gap-2'}>
                                    <p className={'w-1/4 text-sm font-medium text-gray-900'}>
                                        Tracking
                                    </p>
                                    <p className={'w-3/4 text-sm font-medium text-blue-600'}>
                                        {proof.tracking_number ?? 'N/A'}
                                    </p>
                                </div>
                                {proof.status === 'failed' && (
                                    <AlertNotification.Root type="warning">
                                        <AlertNotification.Icon />
                                        <AlertNotification.Content>
                                            <AlertNotification.Title>
                                                There was a problem completing this order.
                                            </AlertNotification.Title>
                                            <AlertNotification.Description>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    color="neutral"
                                                    disabled={isRetryingProofOrder}
                                                    onClick={() => {
                                                        retryProof(proof.id);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faRefresh} />
                                                    Retry
                                                </Button>
                                            </AlertNotification.Description>
                                        </AlertNotification.Content>
                                    </AlertNotification.Root>
                                )}
                                {proof.status !== 'failed' &&
                                    proof.stripe_checkout_session_id === null && (
                                        <div className="text-sm text-blue-500">
                                            Free proof courtesy of Givebutter
                                        </div>
                                    )}
                                {proof.stripe_checkout_session_id !== null &&
                                    proof.payment_received_at === null && (
                                        <div className="text-sm text-red-500">
                                            Payment has not been received
                                        </div>
                                    )}
                                {proof.stripe_checkout_session_id !== null &&
                                    proof.payment_received_at !== null && (
                                        <Collapsible.Root className="group">
                                            <Collapsible.Trigger asChild>
                                                <Button
                                                    variant="transparent"
                                                    size="sm"
                                                    className="!pl-1"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        className="group-radix-state-open:rotate-90"
                                                    />
                                                    View payment details
                                                </Button>
                                            </Collapsible.Trigger>
                                            <Collapsible.Content>
                                                <Suspense fallback={<Loader />}>
                                                    <LetterProofPaymentDetails
                                                        mailPieceId={proof.id}
                                                    />
                                                </Suspense>
                                            </Collapsible.Content>
                                        </Collapsible.Root>
                                    )}
                            </div>
                            <Separator className="bg-gray-300" />
                        </div>
                    </div>
                ))}
        </div>
    );
};

const LetterProofPaymentDetails = (props: { mailPieceId: number }) => {
    const { mailPieceId } = props;
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { data: checkoutDetails } = useSuspenseQuery({
        queryKey: accountQueryKeys.letterProofCheckoutDetails(accountId, letter.id, mailPieceId),
        queryFn: () => fetchLetterProofCheckoutDetails(accountId, letter.id, mailPieceId),
    });

    return (
        <div className={'flex flex-col gap-4'}>
            <div className={'flex flex-col gap-2 rounded-md bg-gray-50 p-2'}>
                <div className={'flex justify-between gap-2'}>
                    <p className={'text-sm font-medium text-gray-800'}>
                        {capitalize(letter.settings.page_type)} x{' '}
                        {checkoutDetails.checkout_details?.quantity}
                    </p>
                    <p className={'text-sm font-medium text-gray-600'}>
                        {checkoutDetails.checkout_details?.total_amount}
                    </p>
                </div>
                <p className={'text-sm text-gray-600'}>
                    {checkoutDetails.checkout_details?.unit_price} / per
                </p>
                <Separator className={'-mx-2 !w-auto'} />
                <div className={'flex justify-between gap-2'}>
                    <p className={'font-medium text-gray-900'}>Total</p>
                    <p className={'font-medium text-gray-900'}>
                        {checkoutDetails.checkout_details?.total_amount}
                    </p>
                </div>
            </div>

            <div className="flex-col gap-2">
                <p className={'text-sm font-medium text-gray-900'}>Payment details</p>
                <div className={`flex items-center gap-2 text-gray-600`}>
                    <div>
                        <div className={'flex items-center gap-2'}>
                            {checkoutDetails.checkout_details?.payment_method.method_subtype && (
                                <PaymentProcessorIcon
                                    type={
                                        checkoutDetails.checkout_details?.payment_method
                                            .method_subtype
                                    }
                                />
                            )}
                            {capitalize(
                                checkoutDetails.checkout_details?.payment_method.method_subtype,
                            )}{' '}
                            <p>
                                •••• {checkoutDetails.checkout_details?.payment_method.cc_last_four}
                            </p>
                        </div>
                        <div className={'bg-gray-25 rounded-lg p-3'}>
                            <ReadOnlyPaymentDetails.Root
                                transaction={
                                    {
                                        payment_method:
                                            checkoutDetails.checkout_details?.payment_method,
                                    } as unknown as PolymorphicTransaction
                                }
                            >
                                <ReadOnlyPaymentDetails.Method />
                                <ReadOnlyPaymentDetails.CcExpire />
                            </ReadOnlyPaymentDetails.Root>
                        </div>
                    </div>
                </div>
            </div>

            {!checkoutDetails.checkout_details?.invoice_url && <span>Generating receipt</span>}
            {checkoutDetails.checkout_details?.invoice_url && (
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={checkoutDetails.checkout_details?.invoice_url}
                >
                    <Button size={'sm'} variant={'outline'} color={'neutral'}>
                        <p>Download receipt</p>
                        <FontAwesomeIcon className={'text-gray-300'} icon={faDownload} />
                    </Button>
                </a>
            )}
        </div>
    );
};

export { LetterOrderedProofs };
